import React, { useState, useEffect } from 'react';
import { Container, Typography, Grid, Card, CardContent, Button, Box, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Add as AddIcon, Business as BusinessIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';
import { deleteBrand } from '../../api/brands';

const BrandSelectorPage = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get('/api/brands');
        setBrands(response.data.brands || []);
      } catch (error) {
        console.error('Error fetching brands:', error);
        toast.error('Failed to load brands');
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  const handleDeleteClick = (brand) => {
    setBrandToDelete(brand);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const result = await deleteBrand(brandToDelete.id);

      if (result.archived) {
        // Brand was archived - update its status in the list
        setBrands(brands.map(b =>
          b.id === brandToDelete.id ? { ...b, status: 'archived' } : b
        ));
        toast.success(`${brandToDelete.name} archived successfully (contains data)`);
      } else {
        // Brand was deleted - remove from list
        setBrands(brands.filter(b => b.id !== brandToDelete.id));
        toast.success(`${brandToDelete.name} deleted successfully`);
      }

      setDeleteDialogOpen(false);
      setBrandToDelete(null);
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error(error.response?.data?.message || 'Failed to delete brand');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBrandToDelete(null);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Filter brands based on archived status
  const filteredBrands = showArchived
    ? brands
    : brands.filter(b => b.status !== 'archived');

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Select Brand
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Choose a brand to manage campaigns and analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setShowArchived(!showArchived)}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/brands/new')}
          >
            Add Brand
          </Button>
        </Box>
      </Box>

      {filteredBrands.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Brands Yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Get started by adding your first brand
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/brands/new')}
          >
            Add Brand
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredBrands.map((brand) => (
            <Grid item xs={12} md={6} lg={4} key={brand.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {brand.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        size="small"
                        label={
                          brand.status === 'archived' ? 'Archived' :
                          brand.status === 'active' ? 'Active' : 'Setup Pending'
                        }
                        color={
                          brand.status === 'archived' ? 'default' :
                          brand.status === 'active' ? 'success' : 'warning'
                        }
                      />
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(brand)}
                        sx={{ ml: 0.5 }}
                        disabled={brand.status === 'archived'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {brand.profile?.website || 'No website configured'}
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => navigate(`/brands/${brand.id}`)}
                  >
                    Manage Brand
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Delete Brand?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{brandToDelete?.name}</strong>?
            This action cannot be undone and will remove all associated data including campaigns, analytics, and planning information.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete Brand
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BrandSelectorPage;